import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { patchTripMerge } from '../../services/TripsService';
import { PatchTripMergeRequest, PatchTripMergeResponse, TripMergeSettings } from '../../types/Trips';

type Result = UseMutationResult<PatchTripMergeResponse, Error, PatchTripMergeRequest>;

function usePatchTripMerge(
  successCb?: () => void,
  failureCb?: (error: string) => void,
): Result {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['patch-trip-merge'],
    mutationFn: patchTripMerge,
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: ['trip-merge', payload.fleetId] });
      const previous = queryClient.getQueryData<TripMergeSettings>(['trip-merge', payload.fleetId]);
      if (previous) {
        queryClient.setQueryData<TripMergeSettings>(['trip-merge', payload.fleetId], {
          ...previous,
          clientTripMergeEnabled: payload.clientTripMergeEnabled,
        });
      }
      return { previous };
    },
    onSuccess: (data, payload) => {
      queryClient.setQueryData<TripMergeSettings>(['trip-merge', payload.fleetId], (previous) => {
        if (previous) {
          return {
            ...previous,
            clientTripMergeEnabled: data.clientTripMergeEnabled,
          };
        }
        return {
          clientTripMergeEnabled: data.clientTripMergeEnabled,
          isMergedTripAvailable: true,
        };
      });
      successCb?.();
    },
    onError: (error: Error, payload, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['trip-merge', payload.fleetId], context.previous);
      }
      failureCb?.(error.message);
    },
    onSettled: (_data, _error, payload) => {
      queryClient.invalidateQueries({ queryKey: ['trip-merge', payload.fleetId] });
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      queryClient.invalidateQueries({ queryKey: ['trips-lazy'] });
      queryClient.invalidateQueries({ queryKey: ['trips-lazy-latest'] });
      queryClient.invalidateQueries({ queryKey: ['active-trips'] });
      queryClient.invalidateQueries({ queryKey: ['active-trip'] });
      queryClient.invalidateQueries({ queryKey: ['active-trips-lazy'] });
      queryClient.invalidateQueries({ queryKey: ['active-trips-lazy-latest'] });
      queryClient.invalidateQueries({ queryKey: ['trip'] });
      queryClient.invalidateQueries({ queryKey: ['merged-trip'] });
    },
  });
}

export default usePatchTripMerge;
